import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { Credentials } from '../types/api';
import { clearHmacKey, initHmac } from '../utils/hmac-utils';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Store } from '../redux/store';
import { resetReportsData } from '../redux/globalActions';
import { CREDENTIALS, SELECTED_BRANCH_DATA, SERVER_KNOWN_SALT } from '../constants';
import { clearServerInfo, fetchServerInfoRequest } from '../redux/serverInfo/serverInfoIndex';
import { fetchServerInfoApi } from '../services/serverInfoApi';
import { offersClearCache } from '../redux/offers/offerActions';

interface AuthContextValue {
    auth: Credentials | null;
    login: (userData: Credentials) => void;
    logout: () => void;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(null);
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    const syncServerKnownSalt = useCallback(async () => {
        const serverKnownSalt = await fetchServerInfoApi();
        localStorage.setItem(SERVER_KNOWN_SALT, serverKnownSalt);
        return serverKnownSalt;
    }, []);

    // Async load from Keystore-backed secure storage on mount
    useEffect(() => {
        SecureStoragePlugin.get({ key: CREDENTIALS })
            .then(async ({ value }) => {
                if (!value) return;
                const parsed = JSON.parse(value);
                // Keep localStorage in sync so service layers that restore from it can sign requests after app relaunch.
                localStorage.setItem(CREDENTIALS, value);
                setAuth(parsed);
                await syncServerKnownSalt();
                await initHmac(parsed.accessToken ?? parsed.access_token);
            })
            .catch(() => {})
            .finally(() => setIsAuthLoading(false));
    }, [syncServerKnownSalt]);

    const login = useCallback(async (userData) => {
        // Write to both SecureStoragePlugin (native) and plain localStorage (web fallback)
        // so all service/saga code that reads localStorage.getItem(CREDENTIALS) is always current.
        const serialized = JSON.stringify(userData);
        Store.dispatch(fetchServerInfoRequest());
        await SecureStoragePlugin.set({ key: CREDENTIALS, value: serialized });
        localStorage.setItem(CREDENTIALS, serialized);
        setAuth(userData);
        await syncServerKnownSalt();
        // Derive the HMAC key from the new user's token before any signed request fires.
        await initHmac(userData.accessToken ?? userData.access_token);
    }, [syncServerKnownSalt]);

    const logout = useCallback(async () => {
        // Clear HMAC key first so no signed request can go out under the old session.
        clearHmacKey();
        // Wipe cached report data (Snapshot/Sales/Owner-view, Reservations, Chatbot)
        // so the next account that logs in never sees the previous user's numbers.
        Store.dispatch(resetReportsData());
        // Wipe stale offer data from Redux + saga cache so the next session starts clean.
        Store.dispatch(offersClearCache());
        // Explicitly remove from plain localStorage so stale tokens can't linger
        // when SecureStoragePlugin uses a prefixed key on web (e.g. _cap_sec_CREDENTIALS).
        await Promise.allSettled([
            SecureStoragePlugin.remove({ key: CREDENTIALS }),
            SecureStoragePlugin.remove({ key: SELECTED_BRANCH_DATA }),
        ]);
        localStorage.removeItem(SERVER_KNOWN_SALT);
        localStorage.removeItem(CREDENTIALS);
        localStorage.removeItem(SELECTED_BRANCH_DATA);
        Store.dispatch(clearServerInfo());
        setAuth(null);
    }, []);

    return (
        <AuthContext.Provider value={{ auth, login, logout, isAuthenticated: !!auth, isAuthLoading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
